import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ReportContent from "../components/ReportContent";
import Highlights from "../components/Highlights";
import LogFrameDiagram from "../components/LogFrameDiagram";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Download, ShieldCheck, Lock, Filter } from "lucide-react";
import { API } from "../lib/api";

const publicApi = axios.create({ baseURL: API, withCredentials: true });

export default function PublicReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [indicatorFilter, setIndicatorFilter] = useState(null); // null = "All"
  const [dimensions, setDimensions] = useState({ themes: [], regions: [] });
  const [theme, setTheme] = useState(""); // "" = All themes
  const [region, setRegion] = useState(""); // "" = All regions

  const fetchReport = (themeArg = theme, regionArg = region) => {
    const params = {};
    if (themeArg) params.theme = themeArg;
    if (regionArg) params.region = regionArg;
    publicApi
      .get(`/public/report/${token}`, { params })
      .then((r) => { setData(r.data); setNeedsPassword(false); setError(""); })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 401 && detail === "password_required") setNeedsPassword(true);
        else setError(detail || "This share link is invalid or revoked.");
      });
  };
  useEffect(() => { fetchReport(theme, region); }, [token, theme, region]);

  // Fetch the theme + region filter universe once unlocked. The
  // endpoint mirrors the unlock-cookie gate, so this also acts as a
  // "report is reachable" probe; failures are swallowed silently — the
  // chip bar simply hides when no dimensions are available.
  useEffect(() => {
    if (!token || needsPassword) return;
    publicApi
      .get(`/public/report/${token}/dimensions`)
      .then((r) => setDimensions(r.data || { themes: [], regions: [] }))
      .catch(() => setDimensions({ themes: [], regions: [] }));
  }, [token, needsPassword]);

  // Inject Open Graph / Twitter Card meta so social previews show the headline highlight.
  useEffect(() => {
    if (!token) return;
    const ogUrl = `${API}/public/report/${token}/og.png`;
    const pageUrl = window.location.href;
    const programName = data?.program?.name || "Array Engine Impact Report";
    const tags = [
      ["property", "og:title", programName],
      ["property", "og:description", "View the live impact report — numbers verified against source data."],
      ["property", "og:image", ogUrl],
      ["property", "og:image:width", "1200"],
      ["property", "og:image:height", "630"],
      ["property", "og:type", "article"],
      ["property", "og:url", pageUrl],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", programName],
      ["name", "twitter:image", ogUrl],
    ];
    const created = [];
    tags.forEach(([attr, key, value]) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", value);
    });
    const prevTitle = document.title;
    document.title = `${programName} · Array Engine`;
    return () => {
      created.forEach((el) => el.remove());
      document.title = prevTitle;
    };
  }, [token, data?.program?.name]);

  const unlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      await publicApi.post(`/public/report/${token}/unlock`, { password });
      fetchReport();
    } catch (err) {
      setError(err.response?.data?.detail || "Wrong password");
    } finally { setUnlocking(false); }
  };

  const auditFetcher = async (indicator_id, period) => {
    const r = await publicApi.get(`/public/report/${token}/audit`, { params: { indicator_id, period } });
    return r.data;
  };
  const downloadPdf = () => { window.open(`${API}/public/report/${token}/pdf`, "_blank"); };

  // Apply the in-view indicator filter without touching the backend — works
  // because indicators[] / breakdown[] / highlights[] all carry indicator
  // ids the donor can recognise from the chip bar.
  const filteredData = useMemo(() => {
    if (!data || !indicatorFilter) return data;
    const matches = (iid) => iid === indicatorFilter;
    return {
      ...data,
      highlights: (data.highlights || []).filter((h) => matches(h.indicator?.id || h.indicator_id)),
      breakdown: (data.breakdown || []).filter((b) => matches(b.indicator?.id)),
      report: {
        ...data.report,
        blocks: (data.report?.blocks || []).filter((b) => !b.indicator_id || matches(b.indicator_id)),
      },
    };
  }, [data, indicatorFilter]);

  const branding = data?.branding || {};
  const logoSrc = branding.org_logo_url || "/array-logo.png";
  const isOrgLogo = Boolean(branding.org_logo_url);

  if (needsPassword) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <form onSubmit={unlock} className="max-w-md w-full bg-white border border-slate-200 p-10 rounded-sm" data-testid="public-unlock-form">
          <Lock className="w-10 h-10 text-slate-400" />
          <h1 className="font-heading text-2xl mt-4">Password required</h1>
          <p className="text-slate-600 mt-2 text-sm">This shared report is password-protected. Enter the password your contact provided.</p>
          <Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} className="mt-4" placeholder="Password" data-testid="unlock-password-input" required />
          {error && <div className="mt-3 text-red-700 bg-red-50 border border-red-200 p-3 text-sm rounded-sm">{error}</div>}
          <Button type="submit" disabled={unlocking} className="mt-4 w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="unlock-submit-button">{unlocking ? "Unlocking…" : "Unlock"}</Button>
        </form>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center bg-white border border-slate-200 p-10 rounded-sm" data-testid="public-report-error">
          <Lock className="w-10 h-10 mx-auto text-slate-400" />
          <h1 className="font-heading text-2xl mt-4">Link unavailable</h1>
          <p className="text-slate-600 mt-2 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Loading shared report…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logoSrc}
              alt={isOrgLogo ? branding.org_name || "Organization" : "Array Engine"}
              className="h-7 w-auto max-w-[180px] object-contain"
              data-testid={isOrgLogo ? "public-org-logo" : "public-array-logo"}
            />
            {isOrgLogo && branding.org_name && (
              <span className="hidden sm:inline-block text-sm text-slate-700 font-medium" data-testid="public-org-name">
                {branding.org_name}
              </span>
            )}
            {!isOrgLogo && (
              <span className="hidden sm:inline-block label-overline">Public report</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-1 text-xs text-slate-500">
              <ShieldCheck className="w-3 h-3"/> Read-only · token-protected
            </span>
            <Button onClick={downloadPdf} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="public-download-pdf-button">
              <Download className="w-4 h-4 mr-1"/> PDF
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-2 sm:px-6 py-10">
        {((dimensions.themes?.length || 0) > 0 || (dimensions.regions?.length || 0) > 0) && (
          <div className="mb-6 px-2 sm:px-0" data-testid="public-dimension-filters">
            <p className="label-overline mb-3 flex items-center gap-1.5">
              <Filter className="w-3 h-3" /> Slice the report
            </p>
            <div className="flex flex-wrap gap-3">
              {(dimensions.themes?.length || 0) > 0 && (
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <span className="uppercase tracking-wider font-medium text-slate-500">Theme</span>
                  <select
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    className="bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    data-testid="public-theme-select"
                  >
                    <option value="">All themes</option>
                    {dimensions.themes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </label>
              )}
              {(dimensions.regions?.length || 0) > 0 && (
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <span className="uppercase tracking-wider font-medium text-slate-500">Region</span>
                  <select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    data-testid="public-region-select"
                  >
                    <option value="">All regions</option>
                    {dimensions.regions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </label>
              )}
              {(theme || region) && (
                <button
                  onClick={() => { setTheme(""); setRegion(""); }}
                  className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
                  data-testid="public-filters-clear"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        )}
        {(data.indicators?.length || 0) > 1 && (
          <div className="mb-6 px-2 sm:px-0" data-testid="public-filter-bar">
            <p className="label-overline mb-3 flex items-center gap-1.5"><Filter className="w-3 h-3" /> Filter by indicator</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setIndicatorFilter(null)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  !indicatorFilter
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid="public-filter-all"
              >
                All
              </button>
              {data.indicators.map((ind) => (
                <button
                  key={ind.id}
                  onClick={() => setIndicatorFilter(ind.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    indicatorFilter === ind.id
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`public-filter-${ind.id}`}
                  title={ind.description || ind.name}
                >
                  {ind.name}
                </button>
              ))}
            </div>
          </div>
        )}
        {filteredData.highlights?.length > 0 && (
          <div className="mb-6 px-2 sm:px-0">
            <p className="label-overline mb-3">Impact at a glance</p>
            <Highlights items={filteredData.highlights} />
          </div>
        )}
        {data.program?.show_logframe_public && data.indicators && data.indicators.length > 1 && data.indicators.some((i) => i.parent_indicator_id) && (
          <div className="mb-6 px-2 sm:px-0" data-testid="public-logframe-thumb">
            <p className="label-overline mb-3">How impact connects</p>
            <LogFrameDiagram
              indicators={data.indicators}
              breakdownByIid={Object.fromEntries((data.breakdown || []).map((b) => [b.indicator.id, b]))}
            />
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="public-report">
          <ReportContent data={filteredData} report={filteredData.report} auditFetcher={auditFetcher} />
          <div className="px-6 py-4 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
            <span>Numbers shown are aggregated from validated source data. Click any value to inspect the audit trail.</span>
            <span className="label-overline">
              {isOrgLogo ? `${branding.org_name || "Organization"} · ` : ""}Powered by Array Engine
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
